<template>
  <div id="restorePromptConfirmationModal" class="modal fade" tabindex="-1">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isDeactivate ? 'Deactivate Version' : 'Set Version Active' }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" :disabled="restoring"></button>
        </div>
        <div class="modal-body">
          <div v-if="restoring" class="d-flex flex-column align-items-center justify-content-center p-4">
            <div class="spinner-border text-primary mb-3" role="status">
              <span class="visually-hidden">Restoring...</span>
            </div>
            <div class="text-muted">{{ isDeactivate ? 'Deactivating version...' : 'Setting version active...' }}</div>
          </div>
          
          <div v-else-if="prompt">
            <template v-if="isDeactivate">
              <p class="mb-3">
                Are you sure you want to deactivate version <strong>v{{ prompt.version }}</strong>?
              </p>
              <div class="alert alert-warning" role="alert">
                <strong>This will:</strong>
                <ul class="mb-0 mt-2">
                  <li>Deactivate version <strong>v{{ prompt.version }}</strong></li>
                  <li v-if="prompt.isActive">Leave the customer with <strong>0 active prompts</strong></li>
                </ul>
              </div>
            </template>
            <template v-else>
              <p class="mb-3">
                Are you sure you want to set version <strong>v{{ prompt.version }}</strong> as active?
              </p>
              <div class="alert alert-warning" role="alert">
                <strong>This will:</strong>
                <ul class="mb-0 mt-2">
                  <li v-if="currentActiveVersion">Deactivate the current active version <strong>v{{ currentActiveVersion.version }}</strong></li>
                  <li>Activate version <strong>v{{ prompt.version }}</strong></li>
                </ul>
              </div>
            </template>
            <div class="text-muted small">
              <strong>Customer:</strong> {{ customerName }}
            </div>

            <div v-if="errorMessage" class="alert alert-danger mt-3" role="alert">
              {{ errorMessage }}
            </div>
          </div>
        </div>
        <div class="modal-footer" v-if="!restoring && prompt">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="restoring">Cancel</button>
          <button 
            type="button" 
            :class="isDeactivate ? 'btn btn-danger' : 'btn btn-primary'" 
            @click="onConfirm" 
            :disabled="restoring"
          >
            {{ isDeactivate ? 'Deactivate' : 'Set Active' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RestorePromptConfirmationModal',
  props: {
    show: Boolean,
    prompt: {
      type: Object,
      default: null
    },
    currentActiveVersion: {
      type: Object,
      default: null
    },
    customerName: String,
    restoring: {
      type: Boolean,
      default: false
    },
    isDeactivate: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      errorMessage: '',
      currentModal: null,
      modalElement: null,
      closePromise: null,
      closeResolver: null,
    };
  },
  watch: {
    show(val) {
      if (val) {
        this.openModal();
        this.errorMessage = '';
      } else {
        this.closeModal().then(() => {
          this.resetForm();
        });
      }
    }
  },
  mounted() {
    this.modalElement = document.getElementById('restorePromptConfirmationModal');
    this.currentModal = new bootstrap.Modal(this.modalElement);
    if (this.show) {
      this.currentModal.show();
    }
    this.modalElement.addEventListener('hidden.bs.modal', () => {
      // Clean up backdrop when modal is fully hidden
      this.cleanupBackdrop();
      
      // Resolve the close promise if it exists
      if (this.closeResolver) {
        this.closeResolver();
        this.closeResolver = null;
        this.closePromise = null;
      }
      
      this.$emit('close');
    });
  },
  beforeDestroy() {
    if (this.modalElement) {
      this.modalElement.removeEventListener('hidden.bs.modal');
    }
    // Dispose of Bootstrap modal instance
    if (this.currentModal) {
      this.currentModal.dispose();
      this.currentModal = null;
    }
    // Clean up any remaining backdrops
    this.cleanupBackdrop();
  },
  methods: {
    openModal() {
      if (this.currentModal) {
        this.currentModal.show();
      }
    },
    closeModal() {
      if (this.currentModal) {
        // Create a promise that resolves when the modal is fully hidden
        this.closePromise = new Promise((resolve) => {
          this.closeResolver = resolve;
        });
        this.currentModal.hide();
      }
      return this.closePromise || Promise.resolve();
    },
    cleanupBackdrop() {
      // Remove all backdrops (handle nested modals)
      const backdrops = document.querySelectorAll('.modal-backdrop');
      backdrops.forEach(backdrop => {
        backdrop.remove();
      });
      
      // Check if any modals are actually visible
      const visibleModals = Array.from(document.querySelectorAll('.modal')).filter(modal => {
        const style = window.getComputedStyle(modal);
        return style.display !== 'none' && modal.classList.contains('show');
      });
      
      // Only remove modal-open class if no modals are visible
      if (visibleModals.length === 0) {
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      }
    },
    resetForm() {
      this.errorMessage = '';
      // Clean up any pending promises
      if (this.closeResolver) {
        this.closeResolver();
        this.closeResolver = null;
        this.closePromise = null;
      }
    },
    onConfirm() {
      if (!this.prompt) return;
      this.$emit('confirm', this.prompt);
    }
  }
};
</script>

<style scoped>
.alert ul {
  padding-left: 1.5rem;
}
</style>
